import React from 'react';
import { Form, Button, Select, Checkbox } from '@crisellevaldez/speculo-ui';
import { PlusCircle } from 'lucide-react';
import { useOrganizationUsers } from '../../../../hooks/useOrganizationUsers';
import { buildTransferLineOptions } from '../../../../utils/flows/formatTransferNumbers';
import { Flow } from '../../../../hooks/useFlowsData';

const normalizeTransferLine = (line: string) =>
  line.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');

const dedupeTransferLines = (lines: string[]) => {
  const seen = new Set<string>();

  return lines.filter((line) => {
    const normalizedLine = normalizeTransferLine(line);

    if (seen.has(normalizedLine)) {
      return false;
    }

    seen.add(normalizedLine);
    return true;
  });
};

interface FlowTransferLinesProps {
  flow: Flow;
  transferLines: string[];
  onTransferLinesChange: (lines: string[]) => void;
  onAddTeamMemberClick: () => void;
  disabled?: boolean;
  useRoundRobin?: boolean;
  onUseRoundRobinChange?: (value: boolean) => void;
}

export const FlowTransferLines: React.FC<FlowTransferLinesProps> = ({
  flow,
  transferLines,
  onTransferLinesChange,
  onAddTeamMemberClick,
  disabled,
  useRoundRobin,
  onUseRoundRobinChange,
}) => {
  const { users, loading: loadingUsers } = useOrganizationUsers();

  const transferLineOptions = React.useMemo(
    () => (loadingUsers ? [] : buildTransferLineOptions(users, flow?.transfer_numbers)),
    [users, flow?.transfer_numbers, loadingUsers]
  );

  const handleTransferLinesChange = React.useCallback(
    (value: string | string[]) => {
      const nextTransferLines = dedupeTransferLines(Array.isArray(value) ? value : [value]);
      onTransferLinesChange(nextTransferLines);

      if (nextTransferLines.length < 2 && useRoundRobin && onUseRoundRobinChange) {
        onUseRoundRobinChange(false);
      }
    },
    [onTransferLinesChange, onUseRoundRobinChange, useRoundRobin]
  );

  React.useEffect(() => {
    const normalizedTransferLines = dedupeTransferLines(transferLines);

    if (normalizedTransferLines.length !== transferLines.length) {
      onTransferLinesChange(normalizedTransferLines);
    }
  }, [onTransferLinesChange, transferLines]);

  return (
    <Form.Control>
      <Form.Label>Transfer Line(s):</Form.Label>
      <div className="flex gap-2">
        <div className="flex-1" onContextMenu={(event) => event.preventDefault()}>
          <Select
            isLoading={loadingUsers}
            options={transferLineOptions}
            value={transferLines}
            className="min-h-9"
            rows={6}
            onChange={handleTransferLinesChange}
            placeholder="Select team members"
            multiple
            disabled={disabled}
            searchable
          />
        </div>
        <Button
          variant="outline"
          className="w-52 items-center gap-2 h-9"
          onClick={onAddTeamMemberClick}
          disabled={disabled}
        >
          <PlusCircle className="w-5 h-5" />
          Add New
        </Button>
      </div>

      {/* Round Robin Checkbox - only show when there are multiple transfer lines */}
      {transferLines.length > 1 && onUseRoundRobinChange && (
        <div className="mt-4">
          <Checkbox
            id="useRoundRobin"
            checked={useRoundRobin || false}
            onChange={(e) => onUseRoundRobinChange(e.target.checked)}
            label="Enable Round Robin for this Flow"
            disabled={disabled}
          />
        </div>
      )}
    </Form.Control>
  );
};
